import { Controller } from "@hotwired/stimulus"
import Honeybadger from "@honeybadger-io/js"
import { confirmDialog } from "../turbo_confirm"

// Controls the trade details slide-over drawer
// Allows viewing assigned employees and quick add/remove functionality
export default class extends Controller {
  static targets = [
    "searchInput",
    "employeeList",
    "dateModal",
    "dateModalEmployeeName",
    "dateGrid",
    "dateCheckbox",
    "startDateInput",
    "endDateInput",
    "requiredCountInput",
  ]

  static values = {
    taskId: Number,
    tradeId: Number,
    taskTradeId: Number,
    startDate: String,
    endDate: String,
    taskStart: String,
    taskEnd: String,
  }

  connect() {
    // Close on escape key
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundHandleKeydown)

    // Listen for trade assignment changes (e.g., from conflict dialog acceptance)
    this.boundHandleAssignmentChanged = this.handleAssignmentChanged.bind(this)
    document.addEventListener(
      "plantafel:trade-assignment-changed",
      this.boundHandleAssignmentChanged,
    )

    // Listen for trade schedule changes (e.g., from shorten warning dialog)
    this.boundHandleScheduleChanged = this.handleScheduleChanged.bind(this)
    document.addEventListener("plantafel:trade-schedule-changed", this.boundHandleScheduleChanged)

    // Store selected employee for date modal
    this.selectedEmployeeId = null
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleKeydown)
    document.removeEventListener(
      "plantafel:trade-assignment-changed",
      this.boundHandleAssignmentChanged,
    )
    document.removeEventListener(
      "plantafel:trade-schedule-changed",
      this.boundHandleScheduleChanged,
    )
  }

  handleAssignmentChanged(event) {
    // Refresh drawer if the change is for the same task/trade we're showing
    const { taskId, tradeId } = event.detail
    if (taskId === this.taskIdValue && tradeId === this.tradeIdValue) {
      this.refreshDrawer()
    }
  }

  handleScheduleChanged(event) {
    // Refresh drawer if the schedule change is for the same task/trade we're showing
    const { taskId, tradeId } = event.detail
    if (taskId === this.taskIdValue && tradeId === this.tradeIdValue) {
      this.refreshDrawer()
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      if (this.hasDateModalTarget && !this.dateModalTarget.classList.contains("hidden")) {
        this.closeDateModal()
      } else {
        this.close()
      }
    }
  }

  close() {
    // Animate out
    this.element.classList.add("translate-x-full")

    // After animation completes, reset to empty placeholder state
    // (preserving the element so turbo stream target remains available)
    setTimeout(() => {
      // Reset to empty placeholder - remove all content and attributes except id
      this.element.innerHTML = ""
      this.element.className = ""
      this.element.removeAttribute("data-controller")
      this.element.removeAttribute("data-plantafel-trade-drawer-task-id-value")
      this.element.removeAttribute("data-plantafel-trade-drawer-trade-id-value")
      this.element.removeAttribute("data-plantafel-trade-drawer-task-trade-id-value")
      this.element.removeAttribute("data-plantafel-trade-drawer-start-date-value")
      this.element.removeAttribute("data-plantafel-trade-drawer-end-date-value")
      this.element.removeAttribute("data-plantafel-trade-drawer-task-start-value")
      this.element.removeAttribute("data-plantafel-trade-drawer-task-end-value")
    }, 300)
  }

  filterEmployees(event) {
    const query = event.target.value.toLowerCase().trim()

    if (!this.hasEmployeeListTarget) return

    const employees = this.employeeListTarget.querySelectorAll("[data-employee-name]")
    employees.forEach((el) => {
      const name = el.dataset.employeeName || ""
      if (query === "" || name.includes(query)) {
        el.classList.remove("hidden")
      } else {
        el.classList.add("hidden")
      }
    })
  }

  // Update the trade date range
  async updateDateRange() {
    if (!this.hasStartDateInputTarget || !this.hasEndDateInputTarget) return

    const startDate = this.startDateInputTarget.value
    const endDate = this.endDateInputTarget.value

    // Validate dates
    if (!startDate || !endDate) return
    if (new Date(startDate) > new Date(endDate)) {
      alert("Das Startdatum darf nicht nach dem Enddatum liegen.")
      // Reset to current values
      this.startDateInputTarget.value = this.startDateValue
      this.endDateInputTarget.value = this.endDateValue
      return
    }

    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")
    const url = `/plantafel/tasks/${this.taskIdValue}/trades/${this.tradeIdValue}/schedule`

    // Show loading state
    this.startDateInputTarget.disabled = true
    this.endDateInputTarget.disabled = true

    try {
      const response = await fetch(url, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          task_trade: {
            start_date: startDate,
            end_date: endDate,
          },
          view_mode: "vertical_month",
          view_start_date: this.getViewStartDate(),
        }),
      })

      const contentType = response.headers.get("Content-Type") || ""
      const html = await response.text()

      // Check if response contains a warning modal (assignments outside new range)
      if (html.includes("plantafel-trade-shorten-warning-modal")) {
        // Reset to current values since user needs to confirm
        this.startDateInputTarget.value = this.startDateValue
        this.endDateInputTarget.value = this.endDateValue
        // Show the warning modal
        Turbo.renderStreamMessage(html)
        return
      }

      if (contentType.includes("text/vnd.turbo-stream.html")) {
        Turbo.renderStreamMessage(html)
      }

      // Update stored values
      this.startDateValue = startDate
      this.endDateValue = endDate

      // Refresh the drawer to reflect new date range
      this.refreshDrawer()
    } catch (error) {
      Honeybadger.notify(error, {
        context: { component: "plantafel-trade-drawer", action: "update_date_range" },
      })
      // Reset to current values on error
      this.startDateInputTarget.value = this.startDateValue
      this.endDateInputTarget.value = this.endDateValue
    } finally {
      this.startDateInputTarget.disabled = false
      this.endDateInputTarget.disabled = false
    }
  }

  // Update the required employee count for this task-trade
  async updateRequiredCount(_event) {
    if (!this.hasRequiredCountInputTarget) return

    const requiredCount = parseInt(this.requiredCountInputTarget.value, 10)

    // Validate count
    if (isNaN(requiredCount) || requiredCount < 0) {
      this.requiredCountInputTarget.value = 1
      return
    }

    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")
    const url = `/plantafel/tasks/${this.taskIdValue}/trades/${this.tradeIdValue}/required_count`

    // Show loading state
    this.requiredCountInputTarget.disabled = true

    try {
      const response = await fetch(url, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          required_count: requiredCount,
          view_mode: "vertical_month",
          view_start_date: this.getViewStartDate(),
        }),
      })

      const contentType = response.headers.get("Content-Type") || ""
      const html = await response.text()

      if (contentType.includes("text/vnd.turbo-stream.html")) {
        Turbo.renderStreamMessage(html)
      }

      // Refresh the drawer to reflect new count
      this.refreshDrawer()
    } catch (error) {
      Honeybadger.notify(error, {
        context: { component: "plantafel-trade-drawer", action: "update_required_count" },
      })
    } finally {
      if (this.hasRequiredCountInputTarget) {
        this.requiredCountInputTarget.disabled = false
      }
    }
  }

  showDateSelector(event) {
    const employeeId = event.currentTarget.dataset.employeeId
    const employeeName = event.currentTarget.dataset.employeeDisplayName
    if (!employeeId) return

    this.selectedEmployeeId = employeeId

    // Update modal with employee name
    if (this.hasDateModalEmployeeNameTarget) {
      this.dateModalEmployeeNameTarget.textContent = `Mitarbeiter: ${employeeName}`
    }

    // Reset all date buttons to unselected state
    if (this.hasDateCheckboxTarget) {
      this.dateCheckboxTargets.forEach((btn) => {
        btn.dataset.selected = "false"
        btn.classList.remove("bg-bautask-green", "text-white", "border-bautask-green")
        btn.classList.add("border-gray-200")
      })
    }

    // Show modal
    if (this.hasDateModalTarget) {
      this.dateModalTarget.classList.remove("hidden")
    }
  }

  closeDateModal() {
    if (this.hasDateModalTarget) {
      this.dateModalTarget.classList.add("hidden")
    }
    this.selectedEmployeeId = null
  }

  toggleDate(event) {
    const button = event.currentTarget
    const isSelected = button.dataset.selected === "true"

    if (isSelected) {
      button.dataset.selected = "false"
      button.classList.remove("bg-bautask-green", "text-white", "border-bautask-green")
      button.classList.add("border-gray-200")
    } else {
      button.dataset.selected = "true"
      button.classList.add("bg-bautask-green", "text-white", "border-bautask-green")
      button.classList.remove("border-gray-200")
    }
  }

  selectAllDates() {
    if (this.hasDateCheckboxTarget) {
      this.dateCheckboxTargets.forEach((btn) => {
        btn.dataset.selected = "true"
        btn.classList.add("bg-bautask-green", "text-white", "border-bautask-green")
        btn.classList.remove("border-gray-200")
      })
    }
  }

  selectTodayOnly() {
    const today = new Date().toISOString().split("T")[0]
    if (this.hasDateCheckboxTarget) {
      this.dateCheckboxTargets.forEach((btn) => {
        const isToday = btn.dataset.dateValue === today
        btn.dataset.selected = isToday ? "true" : "false"
        if (isToday) {
          btn.classList.add("bg-bautask-green", "text-white", "border-bautask-green")
          btn.classList.remove("border-gray-200")
        } else {
          btn.classList.remove("bg-bautask-green", "text-white", "border-bautask-green")
          btn.classList.add("border-gray-200")
        }
      })
    }
  }

  confirmAddEmployee() {
    if (!this.selectedEmployeeId) return

    // Store employee ID before closing modal (which resets it)
    const employeeId = this.selectedEmployeeId

    // Get selected dates by querying DOM directly (more reliable than Stimulus targets)
    const selectedDates = []
    if (this.hasDateGridTarget) {
      const selectedButtons = this.dateGridTarget.querySelectorAll('button[data-selected="true"]')
      selectedButtons.forEach((btn) => {
        if (btn.dataset.dateValue) {
          selectedDates.push(btn.dataset.dateValue)
        }
      })
    }

    // Sort dates chronologically
    selectedDates.sort()

    if (selectedDates.length === 0) {
      alert("Bitte wählen Sie mindestens einen Tag aus.")
      return
    }

    // Close modal first
    this.closeDateModal()

    // Add employee for each selected date
    this.addEmployeeForDates(employeeId, selectedDates)
  }

  async addEmployeeForDates(employeeId, dates, force = false) {
    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")
    const url = `/plantafel/tasks/${this.taskIdValue}/trades/${this.tradeIdValue}/assign`

    // Show loading state on drawer
    this.element.classList.add("opacity-75", "pointer-events-none")

    try {
      // First, check if there's a mismatch by sending the first date
      const firstDate = dates[0]
      const testData = {
        employee_id: employeeId,
        date: firstDate,
        view_mode: "vertical_month",
        view_start_date: this.getViewStartDate(),
        force: force,
      }

      const testResponse = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(testData),
      })

      const testHtml = await testResponse.text()

      // Check if response contains a mismatch modal (dialog id is plantafel-trade-mismatch-modal)
      if (testHtml.includes("plantafel-trade-mismatch-modal") && !force) {
        // Inject all dates into the modal before rendering
        this.element.classList.remove("opacity-75", "pointer-events-none")

        // Add dates array attribute to the modal dialog
        const modifiedHtml = testHtml.replace(
          /data-plantafel-trade-mismatch-modal-date-value="[^"]*"/,
          `data-plantafel-trade-mismatch-modal-date-value="${dates[0]}" data-plantafel-trade-mismatch-modal-dates-value="${JSON.stringify(dates).replace(/"/g, "&quot;")}"`,
        )

        // Render the modified modal with all dates
        Turbo.renderStreamMessage(modifiedHtml)
        return
      }

      // First date was processed (or forced), render its response
      const testContentType = testResponse.headers.get("Content-Type") || ""
      if (testContentType.includes("text/vnd.turbo-stream.html")) {
        Turbo.renderStreamMessage(testHtml)
      }

      // Process remaining dates
      for (let i = 1; i < dates.length; i++) {
        const date = dates[i]
        const data = {
          employee_id: employeeId,
          date: date,
          view_mode: "vertical_month",
          view_start_date: this.getViewStartDate(),
          force: force,
        }

        const response = await fetch(url, {
          method: "POST",
          credentials: "same-origin",
          headers: {
            "X-CSRF-Token": csrfToken,
            Accept: "text/vnd.turbo-stream.html",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        })

        const contentType = response.headers.get("Content-Type") || ""
        const html = await response.text()

        // Only render the last response to avoid flickering
        if (i === dates.length - 1 && contentType.includes("text/vnd.turbo-stream.html")) {
          Turbo.renderStreamMessage(html)
        }

        // Small delay between requests
        if (i < dates.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 50))
        }
      }

      // Refresh the drawer to show updated assignments
      this.refreshDrawer()
    } catch (error) {
      Honeybadger.notify(error, {
        context: { component: "plantafel-trade-drawer", action: "add_employee" },
      })
    } finally {
      this.element.classList.remove("opacity-75", "pointer-events-none")
    }
  }

  async removeEmployee(event) {
    event.stopPropagation()
    const employeeId = event.currentTarget.dataset.employeeId
    const assignmentIdsJson = event.currentTarget.dataset.assignmentIds
    if (!employeeId || !assignmentIdsJson) return

    const assignmentIds = JSON.parse(assignmentIdsJson)
    if (assignmentIds.length === 0) return

    if (
      !(await confirmDialog("Alle Zuweisungen dieses Mitarbeiters zu diesem Gewerk entfernen?", {
        danger: true,
      }))
    ) {
      return
    }

    // Show loading state
    const card = event.currentTarget.closest(".border")
    if (card) {
      card.classList.add("opacity-50", "pointer-events-none")
    }

    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")

    try {
      // Delete each assignment
      for (const assignmentId of assignmentIds) {
        const url = `/plantafel/trade_day_assignments/${assignmentId}`
        await fetch(url, {
          method: "DELETE",
          credentials: "same-origin",
          headers: {
            "X-CSRF-Token": csrfToken,
            Accept: "text/vnd.turbo-stream.html",
          },
        })
      }

      // Refresh the drawer and calendar
      this.refreshDrawer()
    } catch (error) {
      Honeybadger.notify(error, {
        context: { component: "plantafel-trade-drawer", action: "remove_employee" },
      })
      if (card) {
        card.classList.remove("opacity-50", "pointer-events-none")
      }
    }
  }

  refreshDrawer() {
    const csrfToken = document.head
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content")
    const viewStartDate = this.getViewStartDate()
    const url = `/plantafel/tasks/${this.taskIdValue}/trades/${this.tradeIdValue}/details?view_start_date=${viewStartDate}`

    fetch(url, {
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
    })
      .then(async (response) => {
        const contentType = response.headers.get("Content-Type") || ""
        const html = await response.text()

        if (contentType.includes("text/vnd.turbo-stream.html")) {
          Turbo.renderStreamMessage(html)
        }
      })
      .catch((error) => {
        Honeybadger.notify(error, {
          context: { component: "plantafel-trade-drawer", action: "refresh" },
        })
      })
  }

  getViewStartDate() {
    const urlParams = new URLSearchParams(window.location.search)
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, "0")
    const day = String(now.getDate()).padStart(2, "0")
    return urlParams.get("date") || `${year}-${month}-${day}`
  }
}
